/* ============================================================
   ORO GIFT FINDER — RECOMMENDATION ENGINE
   ============================================================
   Multi-step gift discovery with weighted scoring.
   Reads catalogue from inline JSON; writes results to DOM.

   Scoring inputs:
     - occasion (birthday, anniversary, wedding, ...)
     - recipient (partner, parent, sibling, friend, ...)
     - gender (men, women, any)
     - budget tier (999, 1999, 3999, any)
     - free-text description (parsed for personality keywords)
     - recipient name (for engraving suggestions)

   Output:
     - 1 hero pick + 4 alternates
     - Each pick gets generated reasoning across 3 axes:
         WHY (why this product fits their inputs)
         FEEL (how the recipient will feel receiving it)
         PRECIOUS (what makes the piece uniquely special)
   ============================================================ */

(function () {
  'use strict';

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  function init() {
    var section = document.querySelector('.oro-gf');
    if (!section) return;

    // Load the live product DB (rendered from Shopify collections)
    // and the inline fallback DB (used when the live one is empty
    // or for preview environments outside Shopify).
    function parseDb(el) {
      if (!el) return [];
      try {
        var parsed = JSON.parse(el.textContent);
        return parsed.products || [];
      } catch (e) {
        console.error('[Oro Gift Finder] Failed to parse', el.id, e);
        return [];
      }
    }

    var liveProducts     = parseDb(section.querySelector('#ogf-product-db'));
    var fallbackProducts = parseDb(section.querySelector('#ogf-fallback-db'));

    // Primary catalog. If live is populated (merchant has configured
    // the source collection) we use it; otherwise we use the starter
    // fallback so the finder still works on first install.
    var PRODUCTS = liveProducts.length > 0 ? liveProducts : fallbackProducts;
    var USING_LIVE = liveProducts.length > 0;

    if (PRODUCTS.length === 0) {
      console.warn('[Oro Gift Finder] No products found in either live or fallback DB');
      return;
    }

    // Diagnostic for the merchant: log catalog source
    if (window && window.console) {
      console.info(
        '[Oro Gift Finder] Catalog loaded · ' +
        PRODUCTS.length + ' products · source: ' +
        (USING_LIVE ? 'Shopify collections' : 'inline fallback')
      );
    }

    var ORO_BASE = 'https://orobyetchcraft.com';

    // ============================================================
    // STATE
    // ============================================================
    var state = {
      occasion: null,
      recipient: null,
      gender: null,
      budget: null,
      description: '',
      recipientName: ''
    };

    // ============================================================
    // ELEMENTS
    // ============================================================
    var els = {
      stepOccasion:   section.querySelector('#ogf-step-occasion'),
      stepRecipient:  section.querySelector('#ogf-step-recipient'),
      stepGender:     section.querySelector('#ogf-step-gender'),
      stepDesc:       section.querySelector('#ogf-step-desc'),
      stepLoading:    section.querySelector('#ogf-step-loading'),
      stepResult:     section.querySelector('#ogf-step-result'),

      back2:          section.querySelector('#ogf-back-2'),
      back3:          section.querySelector('#ogf-back-3'),
      back4:          section.querySelector('#ogf-back-4'),

      next3:          section.querySelector('#ogf-next-3'),
      revealBtn:      section.querySelector('#ogf-reveal-btn'),
      resetBtn:       section.querySelector('#ogf-reset-btn'),
      shareBtn:       section.querySelector('#ogf-share-btn'),

      recipientName:  section.querySelector('#ogf-recipient-name'),
      description:    section.querySelector('#ogf-description'),

      visualIdle:     section.querySelector('#ogf-visual-idle'),
      visualActive:   section.querySelector('#ogf-visual-active'),
      visualResult:   section.querySelector('#ogf-visual-result'),

      resultHeadline: section.querySelector('#ogf-result-headline'),
      resultRecipient:section.querySelector('#ogf-result-recipient'),

      loadingTitle:   section.querySelector('#ogf-loading-title'),
      loadingSub:     section.querySelector('#ogf-loading-sub'),

      resultTitle:    section.querySelector('#ogf-result-title'),
      resultIntro:    section.querySelector('#ogf-result-intro'),

      heroCard:       section.querySelector('#ogf-hero-card'),
      heroImg:        section.querySelector('#ogf-hero-img'),
      heroName:       section.querySelector('#ogf-hero-name'),
      heroPrice:      section.querySelector('#ogf-hero-price'),
      heroWhy:        section.querySelector('#ogf-hero-why'),
      heroFeel:       section.querySelector('#ogf-hero-feel'),
      heroPrecious:   section.querySelector('#ogf-hero-precious'),

      altGrid:        section.querySelector('#ogf-alt-grid')
    };

    // ============================================================
    // STEP NAVIGATION
    // ============================================================
    function showStep(name) {
      [els.stepOccasion, els.stepRecipient, els.stepGender, els.stepDesc,
       els.stepLoading, els.stepResult].forEach(function (s) {
        if (s) s.hidden = true;
      });
      var target = {
        occasion: els.stepOccasion,
        recipient: els.stepRecipient,
        gender: els.stepGender,
        desc: els.stepDesc,
        loading: els.stepLoading,
        result: els.stepResult
      }[name];
      if (target) target.hidden = false;

      // Smooth scroll into view
      var finder = section.querySelector('#gift-finder');
      if (finder) {
        var rect = finder.getBoundingClientRect();
        if (rect.top < 0 || rect.top > window.innerHeight - 100) {
          finder.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    }

    // ============================================================
    // CHOICE BUTTONS — wire clicks
    // ============================================================
    section.querySelectorAll('[data-occasion]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        section.querySelectorAll('[data-occasion]').forEach(function (b) {
          b.classList.remove('oro-gf__choice--selected');
        });
        btn.classList.add('oro-gf__choice--selected');
        state.occasion = btn.dataset.occasion;
        // Dismiss the start-here chip once the user has begun
        var startChip = section.querySelector('#ogf-start-chip');
        if (startChip) startChip.classList.add('is-hidden');
        // Auto-advance after a brief delay so user sees selection
        setTimeout(function () { showStep('recipient'); }, 400);
      });
    });

    section.querySelectorAll('[data-recipient]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        section.querySelectorAll('[data-recipient]').forEach(function (b) {
          b.classList.remove('oro-gf__choice--selected');
        });
        btn.classList.add('oro-gf__choice--selected');
        state.recipient = btn.dataset.recipient;
        setTimeout(function () { showStep('gender'); }, 400);
      });
    });

    section.querySelectorAll('[data-gender]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        section.querySelectorAll('[data-gender]').forEach(function (b) {
          b.classList.remove('oro-gf__choice--selected');
        });
        btn.classList.add('oro-gf__choice--selected');
        state.gender = btn.dataset.gender;
        checkStep3Ready();
      });
    });

    section.querySelectorAll('[data-budget]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        section.querySelectorAll('[data-budget]').forEach(function (b) {
          b.classList.remove('oro-gf__choice--selected');
        });
        btn.classList.add('oro-gf__choice--selected');
        state.budget = btn.dataset.budget;
        checkStep3Ready();
      });
    });

    function checkStep3Ready() {
      if (state.gender && state.budget) {
        els.next3.disabled = false;
      }
    }

    if (els.next3) {
      els.next3.addEventListener('click', function () {
        if (state.gender && state.budget) showStep('desc');
      });
    }

    // BACK BUTTONS
    if (els.back2) els.back2.addEventListener('click', function () { showStep('occasion'); });
    if (els.back3) els.back3.addEventListener('click', function () { showStep('recipient'); });
    if (els.back4) els.back4.addEventListener('click', function () { showStep('gender'); });

    // RESET
    if (els.resetBtn) {
      els.resetBtn.addEventListener('click', function () {
        state = {
          occasion: null,
          recipient: null,
          gender: null,
          budget: null,
          description: '',
          recipientName: ''
        };
        // Clear selections
        section.querySelectorAll('.oro-gf__choice--selected').forEach(function (b) {
          b.classList.remove('oro-gf__choice--selected');
        });
        if (els.recipientName) els.recipientName.value = '';
        if (els.description) els.description.value = '';
        if (els.next3) els.next3.disabled = true;

        // Reset visual states
        if (els.visualIdle) els.visualIdle.hidden = false;
        if (els.visualActive) els.visualActive.hidden = true;
        if (els.visualResult) els.visualResult.hidden = true;

        // Restore the start-here chip
        var startChip = section.querySelector('#ogf-start-chip');
        if (startChip) startChip.classList.remove('is-hidden');

        showStep('occasion');
      });
    }

    // SHARE
    if (els.shareBtn) {
      els.shareBtn.addEventListener('click', function () {
        var shareText = 'I just used Oro Gift Finder to find the perfect gift! ✦';
        if (navigator.share) {
          navigator.share({
            title: 'My Oro Gift Picks',
            text: shareText,
            url: window.location.href
          }).catch(function () {});
        } else if (navigator.clipboard) {
          navigator.clipboard.writeText(shareText + ' ' + window.location.href).then(function () {
            var orig = els.shareBtn.textContent;
            els.shareBtn.textContent = '✓ Link copied';
            setTimeout(function () { els.shareBtn.textContent = orig; }, 2000);
          });
        }
      });
    }

    // ============================================================
    // REVEAL — main analysis
    // ============================================================
    if (els.revealBtn) {
      els.revealBtn.addEventListener('click', function () {
        state.description = (els.description && els.description.value || '').trim();
        state.recipientName = (els.recipientName && els.recipientName.value || '').trim();

        showStep('loading');
        if (els.visualIdle) els.visualIdle.hidden = true;
        if (els.visualActive) els.visualActive.hidden = false;
        if (els.visualResult) els.visualResult.hidden = true;

        // Scripted loading messages — feel like a real "consultation"
        var stages = [
          { title: 'Reading the constellation...',  sub: 'Aligning the gifts to their orbit' },
          { title: 'Tuning to ' + (state.recipientName || 'their') + ' frequency', sub: 'Decoding personality signals' },
          { title: 'Sweeping the gift universe',    sub: 'Scanning 200+ pieces' },
          { title: 'Calibrating preciousness',      sub: 'Weighing meaning over price' },
          { title: 'Aligning the final five',       sub: 'Composing your gift constellation' }
        ];
        var i = 0;
        if (els.loadingTitle) els.loadingTitle.textContent = stages[0].title;
        if (els.loadingSub)   els.loadingSub.textContent   = stages[0].sub;

        var iv = setInterval(function () {
          i++;
          if (i < stages.length) {
            if (els.loadingTitle) els.loadingTitle.textContent = stages[i].title;
            if (els.loadingSub)   els.loadingSub.textContent   = stages[i].sub;
          } else {
            clearInterval(iv);
            var picks = recommend();
            renderResult(picks);
          }
        }, 950);
      });
    }

    // ============================================================
    // SCORING ENGINE
    // ============================================================
    function recommend() {
      var keywords = parseDescription(state.description);
      var budget = budgetRange(state.budget);

      var scored = PRODUCTS.map(function (p) {
        return { product: p, score: scoreProduct(p, state, keywords, budget) };
      });

      // Strict pass — drop anything that scored 0 (failed a hard filter)
      scored = scored.filter(function (s) { return s.score > 0; });
      scored.sort(function (a, b) { return b.score - a.score; });

      // If we have fewer than 5, broaden — but ONLY relax occasion/recipient/gender,
      // never the budget range. People who set a budget mean it.
      if (scored.length < 5) {
        var fallback = PRODUCTS.map(function (p) {
          return { product: p, score: scoreProduct(p, state, keywords, budget, true) };
        }).sort(function (a, b) { return b.score - a.score; });

        var existingHandles = scored.map(function (s) { return s.product.handle; });
        for (var i = 0; i < fallback.length && scored.length < 5; i++) {
          if (existingHandles.indexOf(fallback[i].product.handle) === -1 && fallback[i].score > 0) {
            scored.push(fallback[i]);
          }
        }
      }

      return scored.slice(0, 5).map(function (s) { return s.product; });
    }

    function budgetRange(b) {
      // Real ranges with both floor and ceiling (with small flex)
      // Flex allows products that are e.g. ₹2050 to qualify for the "under ₹2000" tier
      if (b === 'under-1000') return { min: 0,    max: 1050, label: 'Under ₹1,000' };
      if (b === '1000-2000')  return { min: 950,  max: 2100, label: '₹1,000 – ₹2,000' };
      if (b === '2000-4000')  return { min: 1900, max: 4200, label: '₹2,000 – ₹4,000' };
      // 'any' or null → no constraint
      return { min: 0, max: 999999, label: 'No Limit' };
    }

    // Helper: does product have a tag matching this signal?
    // Used as a fallback when the merchant hasn't put the product
    // in the explicitly-mapped collection but has tagged it accordingly.
    function hasMatchingTag(product, signal) {
      if (!product.tags || !product.tags.length || !signal) return false;
      var s = String(signal).toLowerCase();
      for (var i = 0; i < product.tags.length; i++) {
        var t = String(product.tags[i]).toLowerCase();
        if (t === s || t.indexOf(s) !== -1 || s.indexOf(t) !== -1) return true;
      }
      return false;
    }

    function scoreProduct(p, st, kw, budget, lenient) {
      var score = 0;

      // OCCASION MATCH — big weight (this is why they're shopping)
      // Tier 1: product is in the merchant's mapped occasion collection (+40)
      // Tier 2: product has a Shopify tag matching the occasion         (+20)
      // Tier 3: no signal                                                (hard filter unless lenient)
      if (st.occasion) {
        if (p.occasion && p.occasion.indexOf(st.occasion) !== -1) {
          score += 40;
        } else if (hasMatchingTag(p, st.occasion)) {
          score += 20;
        } else if (!lenient) {
          return 0;
        }
      }

      // RECIPIENT MATCH
      // Tier 1: in mapped recipient collection (+30)
      // Tier 2: has matching Shopify tag       (+15)
      // Tier 3: no signal                       (hard filter unless lenient)
      if (st.recipient) {
        if (p.recipient && p.recipient.indexOf(st.recipient) !== -1) {
          score += 30;
        } else if (hasMatchingTag(p, st.recipient)) {
          score += 15;
        } else if (!lenient) {
          return 0;
        }
      }

      // GENDER MATCH
      // Same tier logic, plus an "any" fallback:
      // products with no gender tag at all are treated as unisex.
      if (st.gender) {
        if (p.gender && p.gender.indexOf(st.gender) !== -1) {
          score += 25;
        } else if (p.gender && p.gender.indexOf('any') !== -1) {
          // Unisex products show up under both men and women
          score += 12;
        } else if (st.gender !== 'any' && hasMatchingTag(p, st.gender)) {
          score += 10;
        } else if (!lenient) {
          return 0;
        }
      }

      // BUDGET RANGE — ALWAYS a hard filter, even on lenient pass.
      // If a user said "₹2K – ₹4K", we must NEVER show them a ₹1199 item.
      // The budget is a promise to the user about price relevance.
      if (st.budget && st.budget !== 'any' && budget) {
        if (p.price < budget.min || p.price > budget.max) {
          return 0; // Out of range — disqualified
        }
        // Sweet spot scoring within the range:
        // Reward products that sit in the upper-middle of the chosen tier
        // (people who select ₹2K-₹4K usually want a substantial gift, not the cheapest qualifier)
        var range = budget.max - budget.min;
        var positionInRange = (p.price - budget.min) / range;
        // Best position is 50–85% of the range (premium-feeling but not maxed out)
        if (positionInRange >= 0.5 && positionInRange <= 0.85) {
          score += 15;
        } else if (positionInRange > 0.85) {
          score += 10; // top of range — still strong
        } else if (positionInRange >= 0.25) {
          score += 8;  // lower half of range — fine
        } else {
          score += 4;  // bottom of range — qualifies but isn't the most aligned
        }
      } else if (st.budget === 'any' || !st.budget) {
        // No budget constraint — small bonus for mid-range products (₹1500–₹2500)
        if (p.price >= 1500 && p.price <= 2500) score += 6;
      }

      // KEYWORD MATCH from description
      if (kw && kw.length) {
        var allTags = (p.tags || []).concat(p.personality || []);
        var matches = 0;
        kw.forEach(function (k) {
          for (var i = 0; i < allTags.length; i++) {
            if (allTags[i].toLowerCase().indexOf(k) !== -1 || k.indexOf(allTags[i].toLowerCase()) !== -1) {
              matches++;
              break;
            }
          }
        });
        score += matches * 8;
      }

      // BESTSELLER BONUS — small, doesn't dominate.
      // On live Shopify data, also check for a "bestseller" or "best-seller" tag.
      if (p.bestseller || hasMatchingTag(p, 'bestseller') || hasMatchingTag(p, 'best-seller')) {
        score += 5;
      }

      // RATING / SOCIAL PROOF — log-scaled so it doesn't dominate
      if (p.rating && p.rating > 0) {
        score += Math.min(10, Math.log10(p.rating + 1) * 5);
      }

      // AVAILABILITY — hard penalty for sold-out items.
      // We don't disqualify outright (some merchants restock quickly),
      // but we push them to the bottom so available items appear first.
      if (p.available === false) {
        score -= 50;
      }

      return score;
    }

    function parseDescription(desc) {
      if (!desc) return [];
      var lower = desc.toLowerCase();
      var keywords = [];

      // Map of detection words -> canonical tags in our DB
      var dict = {
        'minimalist':  ['minimal', 'simple', 'subtle', 'understated', 'minimalist'],
        'bold':        ['bold', 'statement', 'big', 'chunky', 'thick'],
        'spiritual':   ['spiritual', 'religious', 'devot', 'pray', 'temple', 'puja'],
        'evil-eye':    ['evil eye', 'nazar', 'protection', 'negative'],
        'romantic':    ['romantic', 'love', 'lover', 'romance'],
        'couple':      ['couple', 'matching', 'us', 'together', 'his and her', 'his & her'],
        'celestial':   ['moon', 'star', 'celestial', 'cosmic', 'galaxy', 'astrology'],
        'zodiac':      ['zodiac', 'sign', 'horoscope', 'libra', 'aries', 'cancer', 'leo', 'virgo', 'taurus', 'gemini', 'pisces', 'capricorn', 'aquarius', 'scorpio', 'sagittarius'],
        'delicate':    ['delicate', 'dainty', 'small', 'tiny', 'feminine', 'soft'],
        'elegant':     ['elegant', 'classy', 'sophisticated', 'refined', 'chic'],
        'luxury':      ['luxury', 'premium', 'expensive', 'high-end', 'best'],
        'engraved':    ['engrav', 'name', 'initial', 'message', 'date', 'personali'],
        'silver':      ['silver', 'white', 'cool tone'],
        'modern':      ['modern', 'contemporary', 'trendy', 'fashion'],
        'butterfly':   ['butterfly'],
        'leaf':        ['leaf', 'nature', 'earth', 'plant'],
        'heart':       ['heart', 'love'],
        'cross':       ['cross', 'christian', 'church'],
        'kada':        ['kada', 'kadaa', 'bangle']
      };

      Object.keys(dict).forEach(function (canonical) {
        var triggers = dict[canonical];
        for (var i = 0; i < triggers.length; i++) {
          if (lower.indexOf(triggers[i]) !== -1) {
            keywords.push(canonical);
            break;
          }
        }
      });

      return keywords;
    }

    // ============================================================
    // REASONING GENERATOR
    // ============================================================

    var OCCASION_LABEL = {
      birthday: 'birthday', anniversary: 'anniversary', wedding: 'wedding',
      engagement: 'engagement', valentines: "Valentine's Day",
      rakhi: 'Raksha Bandhan', graduation: 'graduation',
      farewell: 'farewell', thankyou: 'thank-you moment', justbecause: 'just-because moment'
    };

    var RECIPIENT_LABEL = {
      partner: 'partner', parent: 'parent', sibling: 'sibling',
      friend: 'best friend', colleague: 'colleague', child: 'child',
      self: 'yourself', other: 'someone special'
    };

    var RECIPIENT_PRONOUN = {
      partner: 'they', parent: 'they', sibling: 'they', friend: 'they',
      colleague: 'they', child: 'they', self: 'you', other: 'they'
    };

    var RECIPIENT_FEEL_TEMPLATES = {
      partner: [
        "The first morning {pronoun} wear it, the smile won't be forced — it'll be the kind that catches in your photos. Years later, this will be the piece {pronoun}'ll touch absent-mindedly while telling someone the story of you two.",
        "It will become part of {pronoun}'s daily ritual — slipped on with the morning coffee, removed only at night. The kind of intimate object that says 'I am loved' without needing words."
      ],
      parent: [
        "Parents rarely buy themselves something this thoughtful — they save, they sacrifice, they put others first. When {pronoun} unwrap this, the surprise will land twice: once for the gift, once for being seen.",
        "Years from now, when {pronoun} catch their reflection wearing it, {pronoun}'ll remember exactly who gave it. That's the kind of weight a parent's gift carries."
      ],
      sibling: [
        "There's a specific kind of joy in a sibling gift — half affection, half competition for who's the more thoughtful one. This wins. Quietly. Permanently.",
        "{pronoun}'ll wear this and someone will ask where it's from. {pronoun}'ll say your name without thinking. That's how this becomes part of your story together."
      ],
      friend: [
        "Best friends notice the small things. The fact that you knew the right occasion, picked the right piece, added the right name — that lands harder than any expensive impulse buy.",
        "This is the kind of gift that gets a long pause, then a quiet 'you remembered.' That's the moment friendship is made of."
      ],
      colleague: [
        "Professional gifting is hard — too cold and it's a gesture, too warm and it's awkward. This sits exactly in the middle: thoughtful, polished, appropriate.",
        "{pronoun} will wear this to meetings, to interviews, to the next chapter of {pronoun}'s career. A clean, lasting piece carries you with it."
      ],
      child: [
        "Children sense when a gift is theirs alone — not a hand-me-down, not generic, not 'for kids'. The engraving is what makes this real for them.",
        "This becomes one of those pieces that travels with them — kept in a small box, brought out at milestones, eventually passed along when {pronoun} have their own."
      ],
      self: [
        "Self-gifting isn't indulgence; it's permission. When you put this on, you're telling yourself the story matters. You showed up. You marked the moment.",
        "There's a quiet courage in choosing for yourself. This piece will be the witness to whatever you're moving toward."
      ],
      other: [
        "A great gift bypasses small talk. The moment {pronoun} open this, the conversation gets real — about meaning, about being thought of, about being seen.",
        "{pronoun}'ll wear this and the story becomes part of who {pronoun} are. That's what jewellery does when it's chosen with care."
      ]
    };

    function generateReasoning(product, st, kw) {
      var occLabel = OCCASION_LABEL[st.occasion] || 'special moment';
      var recipLabel = RECIPIENT_LABEL[st.recipient] || 'someone';
      var pronoun = RECIPIENT_PRONOUN[st.recipient] || 'they';
      var name = st.recipientName ? st.recipientName : null;

      // ---- WHY ----
      var whyParts = [];

      // Lead with strongest match signal
      if (product.occasion && product.occasion.indexOf(st.occasion) !== -1) {
        whyParts.push('Specifically chosen for ' + (occLabel === 'just-because moment' ? 'an unprompted, just-because moment' : 'a ' + occLabel));
      }

      // Personality / keyword overlap
      var personalityMatches = [];
      if (kw && kw.length && product.personality) {
        kw.forEach(function (k) {
          if (product.personality.indexOf(k) !== -1) personalityMatches.push(k);
          else if (product.tags && product.tags.indexOf(k) !== -1) personalityMatches.push(k);
        });
      }
      if (personalityMatches.length) {
        whyParts.push('matches the ' + listJoin(personalityMatches.slice(0, 2)) + ' tone you described');
      } else if (product.personality && product.personality.length) {
        whyParts.push('its ' + listJoin(product.personality.slice(0, 2)) + ' character suits the ' + recipLabel + ' bond');
      }

      // Engraving angle
      if (product.tags && product.tags.indexOf('engraved') !== -1) {
        if (name) {
          whyParts.push("we'd engrave '" + name + "' on it — a permanent line of you in the piece");
        } else {
          whyParts.push('the engraving makes it impossible to mistake for anyone else');
        }
      }

      var why = capitalize(whyParts.join('. ')) + '.';

      // ---- FEEL ----
      var feelTemplates = RECIPIENT_FEEL_TEMPLATES[st.recipient] || RECIPIENT_FEEL_TEMPLATES.other;
      var feelTpl = feelTemplates[hashIdx(product.handle, feelTemplates.length)];
      var feel = feelTpl.replace(/\{pronoun\}/g, pronoun);
      // Capitalize first letter after replacement
      feel = feel.charAt(0).toUpperCase() + feel.slice(1);

      // ---- PRECIOUSNESS ----
      var preciousParts = [];

      // Material angle
      if (product.tags && product.tags.indexOf('silver') !== -1) {
        preciousParts.push('Crafted in silver-finished steel — built to last decades, not seasons');
      } else if (product.price >= 2000) {
        preciousParts.push('22-carat gold-plated on stainless steel, hand-finished by artisans trained for over 100 hours');
      } else {
        preciousParts.push('22-carat gold-plated on premium 316L stainless steel — anti-fade, hypoallergenic, made for everyday wear');
      }

      // Engraving precision
      if (product.tags && product.tags.indexOf('engraved') !== -1) {
        preciousParts.push('precision laser-engraving means the inscription will outlast the trend');
      }

      // Packaging promise
      preciousParts.push('arrives in Oro\'s signature gift box, free wrapping included');

      var precious = preciousParts.join('. ') + '.';

      return { why: why, feel: feel, precious: precious };
    }

    function generateAltWhy(product, st) {
      var bits = [];
      if (product.bestseller) bits.push('Loved by ' + (product.rating || 'many') + '+ buyers');
      else if (product.rating > 50) bits.push(product.rating + '+ five-star reviews');

      if (product.personality && product.personality.length) {
        bits.push(capitalize(product.personality[0]) + ' tone');
      }

      if (product.compare && product.compare > product.price) {
        var pct = Math.round((1 - product.price / product.compare) * 100);
        bits.push(pct + '% off');
      }

      if (!bits.length) bits.push('A solid alternative pick');
      return bits.join(' · ');
    }

    function generateIntro(picks, st) {
      var occLabel = OCCASION_LABEL[st.occasion] || 'this moment';
      var recipLabel = RECIPIENT_LABEL[st.recipient] || 'them';
      var name = st.recipientName ? st.recipientName : null;

      if (name) {
        return "Five gifts, weighed for " + name + "'s " + occLabel + ". The top match is the one we'd send first — the others are equally thoughtful, just different in tone.";
      }
      return "Five gifts, weighed for your " + recipLabel + "'s " + occLabel + ". The top match is the one we'd send first — the others are equally thoughtful, just different in tone.";
    }

    // ============================================================
    // RENDER RESULT
    // ============================================================
    function renderResult(picks) {
      // Honest empty state — never silently fill with off-budget items.
      // The user's budget is a promise about price relevance.
      if (!picks || !picks.length) {
        renderEmptyResult();
        return;
      }

      var keywords = parseDescription(state.description);
      var hero = picks[0];
      var alts = picks.slice(1, 5);

      // Switch visual to result mode
      if (els.visualActive) els.visualActive.hidden = true;
      if (els.visualResult) els.visualResult.hidden = false;
      if (els.resultHeadline) {
        els.resultHeadline.textContent = state.recipientName ? 'For ' + state.recipientName : 'Aligned';
      }
      if (els.resultRecipient) {
        var occLabel = OCCASION_LABEL[state.occasion] || '';
        var recipLabel = RECIPIENT_LABEL[state.recipient] || '';
        els.resultRecipient.textContent = recipLabel + ' · ' + occLabel;
      }

      // Result header
      if (els.resultTitle) {
        var titleText = state.recipientName
          ? "For " + escapeHtml(state.recipientName) + ", we'd choose <em>this</em>."
          : "These will <em>land</em>.";
        els.resultTitle.innerHTML = titleText;
      }
      if (els.resultIntro) {
        els.resultIntro.textContent = generateIntro(picks, state);
      }

      // HERO PICK
      var heroReason = generateReasoning(hero, state, keywords);
      if (els.heroCard) els.heroCard.href = ORO_BASE + '/products/' + hero.handle + buildAttribution();
      if (els.heroImg) {
        els.heroImg.src = hero.image;
        els.heroImg.alt = hero.name;
        els.heroImg.onerror = function () { this.src = fallbackImage(); };
      }
      if (els.heroName) els.heroName.textContent = hero.name;
      if (els.heroPrice) {
        if (hero.compare && hero.compare > hero.price) {
          els.heroPrice.innerHTML = '₹' + hero.price.toLocaleString('en-IN') +
            ' <span style="text-decoration: line-through; opacity: 0.5; font-weight: 400; margin-left: 8px;">₹' +
            hero.compare.toLocaleString('en-IN') + '</span>';
        } else {
          els.heroPrice.textContent = '₹' + hero.price.toLocaleString('en-IN');
        }
      }
      if (els.heroWhy) els.heroWhy.textContent = heroReason.why;
      if (els.heroFeel) els.heroFeel.textContent = heroReason.feel;
      if (els.heroPrecious) els.heroPrecious.textContent = heroReason.precious;

      // ALTERNATES
      if (els.altGrid) {
        els.altGrid.innerHTML = alts.map(function (p, idx) {
          var why = generateAltWhy(p, state);
          var priceHtml = p.compare && p.compare > p.price
            ? '₹' + p.price.toLocaleString('en-IN') +
              ' <span style="text-decoration: line-through; opacity: 0.5; font-weight: 400; font-size: 11px; margin-left: 6px;">₹' +
              p.compare.toLocaleString('en-IN') + '</span>'
            : '₹' + p.price.toLocaleString('en-IN');
          return '<a class="oro-gf__alt-card" href="' + ORO_BASE + '/products/' + p.handle + buildAttribution() + '" target="_blank" rel="noopener">'
              + '<span class="oro-gf__alt-card-rank">' + (idx + 2) + '</span>'
              + '<div class="oro-gf__alt-card-img"><img src="' + p.image + '" alt="' + escapeHtml(p.name) + '" loading="lazy" data-oro-fb="' + fallbackImage() + '" onerror="if(this.src!==this.dataset.oroFb){this.src=this.dataset.oroFb;}"/></div>'
              + '<p class="oro-gf__alt-card-name">' + escapeHtml(p.name) + '</p>'
              + '<p class="oro-gf__alt-card-price">' + priceHtml + '</p>'
              + '<p class="oro-gf__alt-card-why">' + escapeHtml(why) + '</p>'
            + '</a>';
        }).join('');
      }

      showStep('result');
    }

    // Honest empty state — when budget+filters return nothing.
    // We tell the user clearly and offer to widen their budget instead of
    // silently showing items outside their stated range.
    function renderEmptyResult() {
      var budget = budgetRange(state.budget);
      var occLabel = OCCASION_LABEL[state.occasion] || 'this occasion';
      var recipLabel = (RECIPIENT_LABEL[state.recipient] || 'them').toLowerCase();

      if (els.resultHeadline) els.resultHeadline.textContent = 'No perfect fits';
      if (els.resultRecipient) {
        els.resultRecipient.textContent = budget.label + ' · ' + (recipLabel);
      }
      if (els.resultTitle) {
        els.resultTitle.innerHTML = "Honest answer: <em>nothing</em> aligns perfectly.";
      }
      if (els.resultIntro) {
        els.resultIntro.textContent =
          "Within " + budget.label + ", we don't have a piece that fully matches a " +
          occLabel.toLowerCase() + " gift for " + recipLabel +
          ". We'd rather tell you the truth than show you a near-miss.";
      }

      // Suggest widening: try without budget, see what would have qualified
      var widerPicks = PRODUCTS.map(function (p) {
        var savedBudget = state.budget;
        state.budget = 'any';
        var s = scoreProduct(p, state, parseDescription(state.description), budgetRange('any'));
        state.budget = savedBudget;
        return { product: p, score: s };
      }).filter(function (s) { return s.score > 0; })
        .sort(function (a, b) { return b.score - a.score; })
        .slice(0, 3);

      // Replace hero card area with a "Widen your budget?" prompt
      if (els.heroCard) {
        els.heroCard.removeAttribute('href');
        els.heroCard.style.cursor = 'default';
        els.heroCard.onclick = function (e) { e.preventDefault(); };
      }
      if (els.heroImg) {
        els.heroImg.src = fallbackImage();
        els.heroImg.alt = '';
      }
      if (els.heroName) els.heroName.textContent = 'Try widening the budget';
      if (els.heroPrice) els.heroPrice.textContent = '';
      if (els.heroWhy) {
        els.heroWhy.textContent = widerPicks.length
          ? "If you stretched the budget, " + widerPicks.length + " piece" + (widerPicks.length > 1 ? 's' : '') + " would align beautifully."
          : "Try a different occasion or recipient combination.";
      }
      if (els.heroFeel) {
        els.heroFeel.textContent = "We won't show you something that doesn't fit just to fill the page.";
      }
      if (els.heroPrecious) {
        els.heroPrecious.textContent = "Tap below to start over and adjust — or browse the wider catalog.";
      }

      // Show wider-budget alternates if any
      if (els.altGrid) {
        if (widerPicks.length) {
          els.altGrid.innerHTML = widerPicks.map(function (entry, idx) {
            var p = entry.product;
            var priceHtml = '₹' + p.price.toLocaleString('en-IN');
            return '<a class="oro-gf__alt-card" href="' + ORO_BASE + '/products/' + p.handle + buildAttribution() + '" target="_blank" rel="noopener">'
                + '<span class="oro-gf__alt-card-rank" style="background:#fbf9f4;border-color:#a88a3d;color:#0f1d3a;">+</span>'
                + '<div class="oro-gf__alt-card-img"><img src="' + p.image + '" alt="' + escapeHtml(p.name) + '" loading="lazy" data-oro-fb="' + fallbackImage() + '" onerror="if(this.src!==this.dataset.oroFb){this.src=this.dataset.oroFb;}"/></div>'
                + '<p class="oro-gf__alt-card-name">' + escapeHtml(p.name) + '</p>'
                + '<p class="oro-gf__alt-card-price">' + priceHtml + '</p>'
                + '<p class="oro-gf__alt-card-why">If you stretched to ' + priceHtml + '</p>'
              + '</a>';
          }).join('');
        } else {
          els.altGrid.innerHTML = '';
        }
      }

      // Visual: switch to result mode
      if (els.visualActive) els.visualActive.hidden = true;
      if (els.visualResult) els.visualResult.hidden = false;

      showStep('result');
    }

    function buildAttribution() {
      var p = new URLSearchParams({
        ref: 'oro-gift-finder',
        occasion: state.occasion || '',
        recipient: state.recipient || '',
        gender: state.gender || ''
      });
      return '?' + p.toString();
    }

    // ============================================================
    // UTILITIES
    // ============================================================
    function escapeHtml(s) {
      if (s == null) return '';
      var div = document.createElement('div');
      div.textContent = String(s);
      return div.innerHTML;
    }
    function capitalize(s) {
      if (!s) return s;
      return s.charAt(0).toUpperCase() + s.slice(1);
    }
    function listJoin(arr) {
      if (!arr.length) return '';
      if (arr.length === 1) return arr[0];
      if (arr.length === 2) return arr[0] + ' and ' + arr[1];
      return arr.slice(0, -1).join(', ') + ', and ' + arr[arr.length - 1];
    }
    function hashIdx(str, mod) {
      var h = 0;
      for (var i = 0; i < str.length; i++) {
        h = ((h << 5) - h) + str.charCodeAt(i);
        h |= 0;
      }
      return Math.abs(h) % mod;
    }
    function fallbackImage() {
      return 'data:image/svg+xml;utf8,' + encodeURIComponent(
        '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200"><rect width="200" height="200" fill="%23f5f1e8"/><circle cx="100" cy="100" r="50" fill="none" stroke="%23a88a3d" stroke-width="0.5" stroke-dasharray="2,4"/><text x="100" y="108" text-anchor="middle" font-family="serif" font-style="italic" font-size="20" fill="%230f1d3a">Oro</text></svg>'
      );
    }
  }
})();
